import { message, notification } from 'antd'
import { Model } from 'dva'
import { OrganisationsService } from '../../Service'
import { FailureHandler } from 'Utils/ApiHelper'

const Blocks: Model = {
  namespace: 'blocks',
  state: {
    list: [],
    editCurrentTab: 'general',
    highlightedBlockId: null,
  },
  effects: {
    *fetchBlocks(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const orgUuid = yield select((state: any) => state.flowLayout.flow?.organisation?.uuid)
      const version = yield select((state: any) => state.flowLayout.version?.version)
      const subFlow = yield select((state: any) => state.flowLayout.subFlow?.id)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsBlocksList, {
          flowUuid: flowUuid,
          organisationUuid: orgUuid,
          flowConfigVersion: version,
          subflow: subFlow,
          fields: ['id', 'name', 'render_info', 'prompt', 'type'],
        })
        onSuccess(response)
        yield put({ type: 'saveBlocksList', payload: response })
      } catch (err) {
        onFailure(err)
        FailureHandler('Error retrieving blocks', err)
      }
      setLoading(false)
    },
    *create(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, payload, addPathConfig },
      { call, put, select }
    ): any {
      setLoading(true)
      const orgUuid = yield select((state: any) => state.flowLayout.flow?.organisation?.uuid)
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      const subFlow = yield select((state: any) => state.flowLayout.subFlow.id)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsBlocksCreate, {
          flowConfigVersion,
          organisationUuid: orgUuid,
          flowUuid,
          requestBody: {
            ...payload,
            name: payload.name ? payload.name : 'New Block',
            subflow: subFlow,
          },
        })
        if (addPathConfig) {
          yield put({
            type: 'paths/create',
            createBlock: true,
            payload: {
              ...addPathConfig.payload,
              render_info: {
                ...addPathConfig.payload?.render_info,
                target: String(response.id),
              },
              destination_block: response.id,
            },
          })
        }
        yield put({
          type: 'addBlockToList',
          payload: response,
        })
        onSuccess(response)
      } catch (e: any) {
        FailureHandler('Error adding block.', e, onFailure)
      }
      setLoading(false)
    },
    *partialUpdate(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, payload, blockId },
      { call, put, select }
    ): any {
      setLoading(true)
      const orgUuid = yield select((state: any) => state.flowLayout.flow?.organisation?.uuid)
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsBlocksPartialUpdate,
          {
            organisationUuid: orgUuid,
            flowUuid,
            flowConfigVersion,
            id: blockId,
            requestBody: payload,
          }
        )
        yield put({ type: 'updateSelectedBlockAndList', payload: response })
        onSuccess(response)
      } catch (e: any) {
        FailureHandler('Error updating the block.', e, onFailure)
      }
      setLoading(false)
    },
    *savePosition(
      { id, position, width, height, onSuccess = () => {} },
      { call, put, select }
    ): any {
      let response
      const orgUuid = yield select((state: any) => state.flowLayout.flow?.organisation?.uuid)
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      try {
        response = yield call(OrganisationsService.organisationsFlowsVersionsBlocksPartialUpdate, {
          organisationUuid: orgUuid,
          flowUuid,
          flowConfigVersion,
          id: id,
          requestBody: {
            render_info: { position: position, width: width, height: height },
          },
        })
        onSuccess(response)
      } catch (e: any) {
        notification.error({
          message: 'Error saving position.',
          description: `${e.response}`,
        })
      }
      yield put({ type: 'updateSelectedBlockAndList', payload: response })
    },
    *destroy(
      { onSuccess = () => {}, onFailure = () => {}, payload, undoRedoType },
      { call, put, select }
    ): any {
      if (payload.type === 'Start') {
        message.error('Cannot delete the Start block!')
        return
      }
      const orgUuid = yield select((state: any) => state.flowLayout.flow?.organisation?.uuid)
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      try {
        yield call(OrganisationsService.organisationsFlowsVersionsBlocksDestroy, {
          organisationUuid: orgUuid,
          flowUuid,
          flowConfigVersion,
          id: payload.id,
        })
        yield put({
          type: 'paths/removeBlockPathsFromList',
          payload: payload.id,
        })
        yield put({ type: 'removeBlocksFromList', payload: [payload] })
        onSuccess()
      } catch (e: any) {
        FailureHandler('Error deleting the block.', e, onFailure)
      }
    },
    *unDeleteBlock({ payload, onSuccess = () => {} }, { call, put, select }): any {
      try {
        const orgUuid = yield select((state: any) => state.flowLayout.flow?.organisation?.uuid)
        const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
        const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsBlocksUndeleteRetrieve,
          {
            organisationUuid: orgUuid,
            flowConfigVersion,
            flowUuid,
            id: payload.id,
          }
        )
        yield put({
          type: 'addBlockToList',
          payload: response,
        })
        yield put({
          type: 'paths/addBlockPathsToList',
          payload: [...response?.inbound_paths, ...response?.outbound_paths],
        })
        onSuccess(response, [...response?.inbound_paths, ...response?.outbound_paths])
      } catch (e: any) {
        notification.error({
          message: 'Error restoring the block.',
          description: `${JSON.stringify(e.response)}`,
        })
        return
      }
    },
    *retrieve(
      { id, setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, select }
    ): any {
      setLoading(true)
      const orgUuid = yield select((state: any) => state.flowLayout.flow?.organisation?.uuid)
      const flowUuid = yield select((state: any) => state.flowLayout.flow?.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version?.version)
      let response
      try {
        response = yield call(OrganisationsService.organisationsFlowsVersionsBlocksRetrieve, {
          organisationUuid: orgUuid,
          flowConfigVersion,
          flowUuid,
          id,
        })
        onSuccess(response)
      } catch (e: any) {
        onFailure(e)
        notification.error({
          message: 'Error fetching block.',
          description: `${e.response}`,
        })
        return
      }
      setLoading(false)
    },
  },
  reducers: {
    setHighlightedBlock(state, action) {
      return {
        ...state,
        highlightedBlockId: action.payload,
      }
    },
    toggleLoading(state, action) {
      return {
        ...state,
        loading: action.payload,
      }
    },
    toggleSetBlockLoading(state, action) {
      return {
        ...state,
        setBlockLoading: action.payload,
      }
    },
    saveBlocksList(state, action) {
      return {
        ...state,
        list: action.payload,
      }
    },
    setSelectedBlock(state, action) {
      return {
        ...state,
        selectedBlock: action.payload,
        editCurrentTab: action.payload?.type === 'Action' ? 'actions' : state.editCurrentTab,
      }
    },
    updateSelectedBlock(state, action) {
      return {
        ...state,
        selectedBlock: {
          ...state.selectedBlock,
          ...action.payload,
        },
      }
    },
    resetSelectedBlock(state) {
      return {
        ...state,
        selectedBlock: null,
      }
    },
    updateSelectedBlockAndList(state, action) {
      return {
        ...state,
        selectedBlock:
          String(state.selectedBlock?.id) === String(action.payload.id)
            ? action.payload
            : state.selectedBlock,
        list: state.list.map((block: any) => {
          if (String(action.payload?.id) === String(block.id)) {
            return {
              id: Number(action.payload.id),
              name: action.payload.name,
              render_info: action.payload.render_info,
              type: action.payload.type,
              prompt: action.payload.prompt,
            }
          }
          return block
        }),
      }
    },
    addBlockToList(state, action) {
      return {
        ...state,
        list: [...state.list, action.payload],
      }
    },
    addBlocksToList(state, action) {
      return {
        ...state,
        list: [...state.list, ...action.payload],
      }
    },
    removeBlocksFromList(state, action) {
      return {
        ...state,
        list: state.list.filter(
          (block: any) => !action.payload.map((b: any) => String(b.id)).includes(String(block.id))
        ),
      }
    },
    setEditCurrentTab(state, action) {
      return {
        ...state,
        editCurrentTab: action.payload,
      }
    },
    resetBlocks(state, action) {
      return {
        ...state,
        list: [],
        editCurrentTab: 'general',
        highlightedBlockId: null,
      }
    },
  },
}

export default Blocks
